import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut
} from 'firebase/auth';
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import swal from 'sweetalert';
import initializeAuthentication from '../config/firebase';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

initializeAuthentication();

const API_URL = 'https://api.paragaia.tn/user';

const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

const useFirebase = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tokenReady, setTokenReady] = useState(false);
  const auth = getAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const publicPaths = ['/', '/signin', '/signup', '/contact', '/products'];

  const logoutAndRedirect = async () => {
    try {
      await axiosInstance.post('/logout');
      await signOut(auth);
    } catch {}
    localStorage.removeItem('access_token');
    localStorage.removeItem('client_id');
    localStorage.removeItem('user_role');
    delete axiosInstance.defaults.headers['Authorization'];
    setUser(null);
    if (!publicPaths.includes(location.pathname)) {
      navigate('/signin');
    }
  };

  const checkTokenValidity = () => {
    const token = localStorage.getItem('access_token');
    if (!token) return false;
    try {
      const decoded = jwtDecode(token);
      return decoded.exp >= Date.now() / 1000;
    } catch {
      return false;
    }
  };

  const fetchUserProfile = async () => {
    try {
      const res = await axiosInstance.get('/me');
      const user = res.data.user;
      const newToken = res.data.token;
      if (newToken) {
        localStorage.setItem('access_token', newToken);
        axiosInstance.defaults.headers['Authorization'] = `Bearer ${newToken}`;
      }
      localStorage.setItem('client_id', user.id);
      localStorage.setItem('user_role', user.role);
      setUser(user);
      setTokenReady(true);
      if (user.role === 'admin') navigate(`/admin/${user.dashboardUrl}`);
      else navigate('/');
    } catch {
      await logoutAndRedirect();
    }
  };

  useEffect(() => {
    const check = async () => {
      const valid = checkTokenValidity();
      if (valid) {
        await fetchUserProfile();
      } else {
        if (!publicPaths.includes(location.pathname)) {
          await logoutAndRedirect();
        } else {
          localStorage.clear();
          setUser(null);
          setTokenReady(true);
        }
      }
      setIsLoading(false);
    };
    check();
  }, []);

  const registerUser = async (email, password, name) => {
    setIsLoading(true);
    try {
      await axiosInstance.post('/register', { email, password, name, role: 'client' });
      const loginRes = await axiosInstance.post('/login', { email, password });
      const { token, user } = loginRes.data;
      if (token) {
        localStorage.setItem('access_token', token);
        localStorage.setItem('client_id', user.id);
        localStorage.setItem('user_role', user.role);
      }
      setUser(user);
      await swal('Succès', 'Compte créé', 'success');
      navigate('/');
    } catch {
      await swal('Erreur', "Échec de l'inscription", 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const signInUser = async (email, password) => {
    setIsLoading(true);
    localStorage.clear();
    try {
      const res = await axiosInstance.post('/login', { email, password });
      const { token, user } = res.data;
      if (token) {
        localStorage.setItem('access_token', token);
        localStorage.setItem('client_id', user.id);
        localStorage.setItem('user_role', user.role);
      }
      setUser(user);
      await swal('Bienvenue', 'Connexion réussie', 'success');
      if (user.role === 'admin') navigate(`/admin/${user.dashboardUrl}`);
      else navigate('/');
      return user;
    } catch (err) {
      const message = err.response?.data?.message || 'Erreur de connexion';
      await swal('Erreur', message, 'error');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    setIsLoading(true);
    const provider = new GoogleAuthProvider();
    try {
      const res = await signInWithPopup(auth, provider);
      const { email, displayName } = res.user;
      const apiRes = await axiosInstance.post('/google-login', {
        email,
        name: displayName,
      });
      const { token, user } = apiRes.data;
      if (token) {
        localStorage.setItem('access_token', token);
        localStorage.setItem('client_id', user.id);
        localStorage.setItem('user_role', user.role);
      }
      setUser(user);
      await swal('Bienvenue', 'Connexion Google réussie', 'success');
      if (user.role === 'admin') navigate(`/admin/${user.dashboardUrl}`);
      else navigate('/');
    } catch {
      await swal('Erreur', 'Échec Google', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const signOutUser = async () => {
    setIsLoading(true);
    try {
      await axiosInstance.post('/logout');
      await signOut(auth);
      localStorage.clear();
      delete axiosInstance.defaults.headers['Authorization'];
      setUser(null);
      await swal('Déconnexion', 'Vous êtes déconnecté', 'success');
      navigate('/signin');
    } catch {
      await swal('Erreur', 'Déconnexion échouée', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return {
    user,
    setUser,
    registerUser,
    signInUser,
    signInWithGoogle,
    signOutUser,
    fetchUserProfile,
    isLoading,
    tokenReady,
  };
};

export default useFirebase;
